import { useCallback, useState } from "react";
import { DEFAULT_PROPERTY_TYPES_FILTERS, OWNER_OCCUPANCY_FILTER, OwnerOccupancyFilter } from "../constants";
import {
  ACREAGE_MIN,
  YEAR_BUILT_MIN,
  ACREAGE_MAX,
  SQFT_MAX,
  SQFT_MIN,
} from "../../../constants/neighborhoodSearch";
import { getYearBuiltMax } from "../../../utils/neighborhoodSearch";

interface UsePropertyFiltersParams {
  initialSelectedPropertyTypes?: string[];
  initialBeds?: (number | undefined)[] | undefined;
  initialBaths?: (number | undefined)[] | undefined;
  initialSqft?: (number | undefined)[];
  initialAcreage?: (number | undefined)[];
  initialYearBuilt?: (number | undefined)[];
  initialIsFavorite?: boolean | undefined | null;
  initialIsShowOnlyPrivatelyOwned?: boolean | undefined | null;
  initialIsHideDNCPhoneNumbers?: boolean | undefined | null;
  initialOwnerOccupied?: OwnerOccupancyFilter;
  initialSearchByTags?: string;
  initialSearchByNotes?: string;
}

export const usePropertyFilters = ({
  initialSelectedPropertyTypes = [],
  initialBeds = undefined,
  initialBaths = undefined,
  initialSqft = [SQFT_MIN, SQFT_MAX],
  initialAcreage = [ACREAGE_MIN, ACREAGE_MAX],
  initialYearBuilt = [YEAR_BUILT_MIN, getYearBuiltMax()],
  initialIsFavorite = undefined,
  initialIsShowOnlyPrivatelyOwned = undefined,
  initialIsHideDNCPhoneNumbers = undefined,
  initialOwnerOccupied = OWNER_OCCUPANCY_FILTER.ALL,
  initialSearchByTags = "",
  initialSearchByNotes = "",
}: UsePropertyFiltersParams = {}) => {

  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>(
    initialSelectedPropertyTypes
  );
  const [beds, setBeds] = useState<(number | undefined)[] | undefined>(initialBeds);
  const [baths, setBaths] = useState<(number | undefined)[] | undefined>(initialBaths);
  const [sqft, setSqft] = useState<(number | undefined)[]>(initialSqft);
  const [acreage, setAcreage] =
    useState<(number | undefined)[]>(initialAcreage);
  const [yearBuilt, setYearBuilt] =
    useState<(number | undefined)[]>(initialYearBuilt);
  const [isFavorite, setIsFavorite] = useState<boolean | undefined | null>(
    initialIsFavorite
  );
  const [isShowOnlyPrivatelyOwned, setIsShowOnlyPrivatelyOwned] = useState<
    boolean | undefined | null
  >(initialIsShowOnlyPrivatelyOwned);
  const [isHideDNCPhoneNumbers, setIsHideDNCPhoneNumbers] = useState<
    boolean | undefined | null
  >(initialIsHideDNCPhoneNumbers);
  const [ownerOccupied, setOwnerOccupied] = useState<OwnerOccupancyFilter>(
    initialOwnerOccupied
  );
  const [searchByTags, setSearchByTags] = useState(initialSearchByTags);
  const [searchByNotes, setSearchByNotes] = useState(initialSearchByNotes);

  const resetToDefaults = useCallback(() => {
    setSelectedPropertyTypes(DEFAULT_PROPERTY_TYPES_FILTERS);
    setBeds(undefined);
    setBaths(undefined);
    setSqft([SQFT_MIN, SQFT_MAX]);
    setAcreage([ACREAGE_MIN, ACREAGE_MAX]);
    setYearBuilt([YEAR_BUILT_MIN, getYearBuiltMax()]);
    setIsFavorite(initialIsFavorite);
    setIsShowOnlyPrivatelyOwned(initialIsShowOnlyPrivatelyOwned);
    setIsHideDNCPhoneNumbers(initialIsHideDNCPhoneNumbers);
    setOwnerOccupied(OWNER_OCCUPANCY_FILTER.ALL);
    setSearchByTags("");
    setSearchByNotes("");
  }, [
    setSelectedPropertyTypes,
    setBeds,
    setBaths,
    setSqft,
    setAcreage,
    setYearBuilt,
    initialIsFavorite,
    initialIsShowOnlyPrivatelyOwned,
    initialIsHideDNCPhoneNumbers,
  ]);

  return {
    selectedPropertyTypes,
    setSelectedPropertyTypes,
    beds,
    setBeds,
    sqft,
    setSqft,
    acreage,
    setAcreage,
    yearBuilt,
    setYearBuilt,
    baths,
    setBaths,
    resetToDefaults,
    isFavorite,
    setIsFavorite,
    isShowOnlyPrivatelyOwned,
    setIsShowOnlyPrivatelyOwned,
    isHideDNCPhoneNumbers,
    setIsHideDNCPhoneNumbers,
    ownerOccupied,
    setOwnerOccupied,
    searchByTags,
    setSearchByNotes,
    searchByNotes,
    setSearchByTags,
  };
};
